import { AccountSettingsRoutes } from 'containers/AccountSettings/AccountSettingsRoutes'
import { AccountLockedOut } from 'containers/AccountLockedOut/AccountLockedOut'
import AccountHome from 'containers/AccountHome'
import { EMSNPage } from 'containers/eMSNs/EMSNPage'
import { MedicareComplaintForm } from 'containers/MedicareComplaintForm/MedicareComplaintForm'
import { Error404 } from 'Error404'
import { Redirect, Route, Switch } from 'react-router-dom'
import { AuthenticatedRoute } from 'components/AuthenticatedRoute/AuthenticatedRoute'
import { FC } from 'react'
import { LaunchDarklyDependentContent } from 'components/LaunchDarklyDependentContent/LaunchDarklyDependentContent'
import { ExternalRedirect } from 'components/ExternalRedirect/ExternalRedirect'
import { useMedicareUrl } from 'hooks/useMedicareUrl'
import { ClaimsSwitch } from 'containers/Claims/ClaimsSwitch'
import { DownloadMyData } from 'containers/DownloadMyData/DownloadMyData'
import { PremiumsSwitch } from 'containers/Premiums/PremiumsSwitch'
import { PreventiveServices } from 'containers/PreventiveServices'
import RxDrugCard from 'containers/RxDrugCard/RxDrugCard'
import { ConnectedApps } from 'containers/ConnectedApps/ConnectedApps'

interface AppRoutesProps {
  showTimeoutMessage: boolean
  setShowTimeoutMessage: (showTimeoutMessage: boolean) => void
}

export const AppRoutes: FC<AppRoutesProps> = ({
  showTimeoutMessage,
  setShowTimeoutMessage
}) => {
  const legacyEMSNUrl = useMedicareUrl(
    'https://www.medicare.gov/mbp/viewmsn.aspx',
    'https://www.medicare.gov/mbp/viewmsn.aspx?Lang=es-mx'
  )

  const legacyPreventiveServicesUrl = useMedicareUrl(
    'https://www.medicare.gov/mbp/mypreventiveservices.aspx',
    'https://www.medicare.gov/mbp/mypreventiveservices.aspx?Lang=es-mx'
  )

  return (
    <Switch>
      <Route path="/claims">
        <ClaimsSwitch />
      </Route>
      <Route path="/premiums">
        <PremiumsSwitch />
      </Route>
      <Route path="/download-my-data">
        <LaunchDarklyDependentContent
          flagName="useNewDownloadMyData"
          fallbackContent={<Error404 />}
        >
          <DownloadMyData />
        </LaunchDarklyDependentContent>
      </Route>
      <Route path="/account-settings" component={AccountSettingsRoutes} />
      <Route path="/connected-apps" component={ConnectedApps}/>
      <Route path="/account-temporarily-locked">
        <LaunchDarklyDependentContent
          flagName="accountSettings"
          fallbackContent={<Error404 />}
        >
          <AccountLockedOut />
        </LaunchDarklyDependentContent>
      </Route>
      <Route
        path="/medicare-complaint"
        // Do not pass router props in directly or else the component re-renders and will go to other steps with invalid data
        render={() => (
          <MedicareComplaintForm
            showTimeoutMessage={showTimeoutMessage}
            setShowTimeoutMessage={setShowTimeoutMessage}
          />
        )}
      />

      <AuthenticatedRoute path="/eMSN" redirectPath="/eMSN">
        <LaunchDarklyDependentContent
          flagName="eMsn"
          fallbackContent={<ExternalRedirect url={legacyEMSNUrl} />}
        >
          <EMSNPage />
        </LaunchDarklyDependentContent>
      </AuthenticatedRoute>

      <AuthenticatedRoute
        path="/preventive-services"
        redirectPath="/preventive-services"
      >
        <LaunchDarklyDependentContent
          flagName="useNewPreventiveServices"
          fallbackContent={
            <ExternalRedirect url={legacyPreventiveServicesUrl} />
          }
        >
          <PreventiveServices />
        </LaunchDarklyDependentContent>
      </AuthenticatedRoute>

      <AuthenticatedRoute path="/home" redirectPath="/home">
        <LaunchDarklyDependentContent
          flagName="useNewAccountHome"
          fallbackContent={<Error404 />}
        >
          <AccountHome />
        </LaunchDarklyDependentContent>
      </AuthenticatedRoute>

      <AuthenticatedRoute path="/rx-drug-card" redirectPath="/rx-drug-card">
        <LaunchDarklyDependentContent
          flagName="useNewRxDrugCard"
          fallbackContent={<Error404 />}
        >
          <RxDrugCard />
        </LaunchDarklyDependentContent>
      </AuthenticatedRoute>

      {/* Default */}
      <Route exact path="/">
        <Redirect to="/home" />
      </Route>

      <Route>
        <Error404 />
      </Route>
    </Switch>
  )
}
